/**
 * FormsService
 * Admin surface for forms: query, CRUD, builder draft/settings, publish lifecycle,
 * versions, share/QR, analytics, collaborators, uniqueness rules, capacity, API access,
 * inbound tokens, admin entry capture and AI helpers.
 * Route: forms/api/forms
 */
import { BaseService } from '../base/BaseService';
import { unwrapFormsResponse as unwrap } from './unwrapFormsResponse';
/** Admin forms service (JWT). */
export class FormsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FormsService.DEFAULT_SERVICE_PATH,
            basePath: FormsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ---- lookups / dashboard ----
    /** GET api/forms/lookups — every Forms lookup table + categories/series + member fields. */
    async getLookups() {
        return unwrap(await this.getLookupsRaw(), 'Forms lookups not found');
    }
    getLookupsRaw() {
        return this.http.get(this.buildUrl('lookups'));
    }
    /** GET api/forms/dashboard */
    async getDashboard() {
        return unwrap(await this.getDashboardRaw(), 'Forms dashboard not found');
    }
    getDashboardRaw() {
        return this.http.get(this.buildUrl('dashboard'));
    }
    // ---- CRUD ----
    /** POST api/forms/query — paged, row-scoped form list. */
    async query(request = {}) {
        return unwrap(await this.queryRaw(request), 'Failed to query forms');
    }
    queryRaw(request = {}) {
        return this.http.post(this.buildUrl('query'), request);
    }
    /** GET api/forms/{id} */
    async getById(id) {
        return unwrap(await this.getByIdRaw(id), `Form ${id} not found`);
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(String(id)));
    }
    /** POST api/forms */
    async create(request) {
        return unwrap(await this.createRaw(request), 'Failed to create form');
    }
    createRaw(request) {
        return this.http.post(this.buildUrl(), request);
    }
    /** PUT api/forms/{id} — header fields (name, slug, dates, flags, owner). */
    async update(id, request) {
        return unwrap(await this.updateRaw(id, request), `Failed to update form ${id}`);
    }
    updateRaw(id, request) {
        return this.http.put(this.buildUrl(String(id)), request);
    }
    /** DELETE api/forms/{id} (soft delete). */
    async remove(id) {
        const response = await this.removeRaw(id);
        return response.data ?? response.success;
    }
    removeRaw(id) {
        return this.http.delete(this.buildUrl(String(id)));
    }
    /** POST api/forms/{id}/duplicate */
    async duplicate(id) {
        return unwrap(await this.duplicateRaw(id), `Failed to duplicate form ${id}`);
    }
    duplicateRaw(id) {
        return this.http.post(this.buildUrl(`${id}/duplicate`), {});
    }
    // ---- builder ----
    /** PUT api/forms/{id}/draft — 409 when expectedDraftVersionNo is stale. */
    async saveDraft(id, request) {
        return unwrap(await this.saveDraftRaw(id, request), `Failed to save draft for form ${id}`);
    }
    saveDraftRaw(id, request) {
        return this.http.put(this.buildUrl(`${id}/draft`), request);
    }
    /** PUT api/forms/{id}/settings */
    async saveSettings(id, request) {
        return unwrap(await this.saveSettingsRaw(id, request), `Failed to save settings for form ${id}`);
    }
    saveSettingsRaw(id, request) {
        return this.http.put(this.buildUrl(`${id}/settings`), request);
    }
    /** POST api/forms/{id}/validate-schema — validates the current draft. */
    async validateSchema(id) {
        return unwrap(await this.validateSchemaRaw(id), `Failed to validate form ${id}`);
    }
    validateSchemaRaw(id) {
        return this.http.post(this.buildUrl(`${id}/validate-schema`), {});
    }
    // ---- lifecycle ----
    /** POST api/forms/{id}/publish — snapshots the draft into a new version. */
    async publish(id, request = {}) {
        return unwrap(await this.publishRaw(id, request), `Failed to publish form ${id}`);
    }
    publishRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`${id}/publish`), request);
    }
    /** POST api/forms/{id}/close */
    async close(id) {
        return unwrap(await this.closeRaw(id), `Failed to close form ${id}`);
    }
    closeRaw(id) {
        return this.http.post(this.buildUrl(`${id}/close`), {});
    }
    /** POST api/forms/{id}/reopen */
    async reopen(id) {
        return unwrap(await this.reopenRaw(id), `Failed to reopen form ${id}`);
    }
    reopenRaw(id) {
        return this.http.post(this.buildUrl(`${id}/reopen`), {});
    }
    /** POST api/forms/{id}/archive */
    async archive(id) {
        return unwrap(await this.archiveRaw(id), `Failed to archive form ${id}`);
    }
    archiveRaw(id) {
        return this.http.post(this.buildUrl(`${id}/archive`), {});
    }
    /** POST api/forms/{id}/unarchive */
    async unarchive(id) {
        return unwrap(await this.unarchiveRaw(id), `Failed to unarchive form ${id}`);
    }
    unarchiveRaw(id) {
        return this.http.post(this.buildUrl(`${id}/unarchive`), {});
    }
    // ---- versions / fields ----
    /** GET api/forms/{id}/versions (no schema). */
    async getVersions(id) {
        return (await this.getVersionsRaw(id)).data ?? [];
    }
    getVersionsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/versions`));
    }
    /** GET api/forms/{id}/versions/{versionId} (with schema). */
    async getVersion(id, versionId) {
        return unwrap(await this.getVersionRaw(id, versionId), `Version ${versionId} of form ${id} not found`);
    }
    getVersionRaw(id, versionId) {
        return this.http.get(this.buildUrl(`${id}/versions/${versionId}`));
    }
    /** GET api/forms/{id}/fields?allVersions= — union by key across versions when allVersions. */
    async getFields(id, allVersions = false) {
        return (await this.getFieldsRaw(id, allVersions)).data ?? [];
    }
    getFieldsRaw(id, allVersions = false) {
        return this.http.get(this.buildUrlWithParams(`${id}/fields`, allVersions ? { allVersions: true } : undefined));
    }
    // ---- share / QR ----
    /** GET api/forms/{id}/share */
    async getShare(id) {
        return unwrap(await this.getShareRaw(id), `Share info for form ${id} not found`);
    }
    getShareRaw(id) {
        return this.http.get(this.buildUrl(`${id}/share`));
    }
    /** POST api/forms/{id}/regenerate-shortcode */
    async regenerateShortCode(id) {
        return unwrap(await this.regenerateShortCodeRaw(id), `Failed to regenerate short code for form ${id}`);
    }
    regenerateShortCodeRaw(id) {
        return this.http.post(this.buildUrl(`${id}/regenerate-shortcode`), {});
    }
    /** POST api/forms/{id}/qr/regenerate */
    async regenerateQr(id) {
        return unwrap(await this.regenerateQrRaw(id), `Failed to regenerate QR for form ${id}`);
    }
    regenerateQrRaw(id) {
        return this.http.post(this.buildUrl(`${id}/qr/regenerate`), {});
    }
    // ---- analytics ----
    /** GET api/forms/{id}/analytics?days= (default 30). */
    async getAnalytics(id, days = 30) {
        return unwrap(await this.getAnalyticsRaw(id, days), `Analytics for form ${id} not found`);
    }
    getAnalyticsRaw(id, days = 30) {
        return this.http.get(this.buildUrlWithParams(`${id}/analytics`, { days }));
    }
    // ---- collaborators ----
    /** GET api/forms/{id}/collaborators */
    async getCollaborators(id) {
        return (await this.getCollaboratorsRaw(id)).data ?? [];
    }
    getCollaboratorsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/collaborators`));
    }
    /** POST api/forms/{id}/collaborators — add or change a collaborator; returns the full list. */
    async upsertCollaborator(id, request) {
        return (await this.upsertCollaboratorRaw(id, request)).data ?? [];
    }
    upsertCollaboratorRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/collaborators`), request);
    }
    /** DELETE api/forms/{id}/collaborators/{collaboratorId} */
    async removeCollaborator(id, collaboratorId) {
        const response = await this.removeCollaboratorRaw(id, collaboratorId);
        return response.data ?? response.success;
    }
    removeCollaboratorRaw(id, collaboratorId) {
        return this.http.delete(this.buildUrl(`${id}/collaborators/${collaboratorId}`));
    }
    // ---- uniqueness rules ----
    /** GET api/forms/{id}/unique-rules */
    async getUniqueRules(id) {
        return (await this.getUniqueRulesRaw(id)).data ?? [];
    }
    getUniqueRulesRaw(id) {
        return this.http.get(this.buildUrl(`${id}/unique-rules`));
    }
    /** POST api/forms/{id}/unique-rules */
    async createUniqueRule(id, request) {
        return unwrap(await this.createUniqueRuleRaw(id, request), `Failed to create unique rule for form ${id}`);
    }
    createUniqueRuleRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/unique-rules`), request);
    }
    /** PUT api/forms/{id}/unique-rules/{ruleId} */
    async updateUniqueRule(id, ruleId, request) {
        return unwrap(await this.updateUniqueRuleRaw(id, ruleId, request), `Failed to update unique rule ${ruleId}`);
    }
    updateUniqueRuleRaw(id, ruleId, request) {
        return this.http.put(this.buildUrl(`${id}/unique-rules/${ruleId}`), request);
    }
    /** DELETE api/forms/{id}/unique-rules/{ruleId} */
    async removeUniqueRule(id, ruleId) {
        const response = await this.removeUniqueRuleRaw(id, ruleId);
        return response.data ?? response.success;
    }
    removeUniqueRuleRaw(id, ruleId) {
        return this.http.delete(this.buildUrl(`${id}/unique-rules/${ruleId}`));
    }
    // ---- capacity ----
    /** GET api/forms/{id}/capacity */
    async getCapacity(id) {
        return (await this.getCapacityRaw(id)).data ?? [];
    }
    getCapacityRaw(id) {
        return this.http.get(this.buildUrl(`${id}/capacity`));
    }
    /** PUT api/forms/{id}/capacity/{bucketId} — body `{ maxUnits }`. */
    async updateCapacityBucket(id, bucketId, maxUnits) {
        return unwrap(await this.updateCapacityBucketRaw(id, bucketId, maxUnits), `Failed to update capacity bucket ${bucketId}`);
    }
    updateCapacityBucketRaw(id, bucketId, maxUnits) {
        return this.http.put(this.buildUrl(`${id}/capacity/${bucketId}`), { maxUnits });
    }
    // ---- API access (managed API keys) ----
    /** GET api/forms/{id}/api-access */
    async getApiAccess(id) {
        return (await this.getApiAccessRaw(id)).data ?? [];
    }
    getApiAccessRaw(id) {
        return this.http.get(this.buildUrl(`${id}/api-access`));
    }
    /** POST api/forms/{id}/api-access — grant (or update) an API key's access to this form. */
    async grantApiAccess(id, request) {
        return unwrap(await this.grantApiAccessRaw(id, request), `Failed to grant API access on form ${id}`);
    }
    grantApiAccessRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/api-access`), request);
    }
    /** DELETE api/forms/{id}/api-access/{accessId} */
    async revokeApiAccess(id, accessId) {
        const response = await this.revokeApiAccessRaw(id, accessId);
        return response.data ?? response.success;
    }
    revokeApiAccessRaw(id, accessId) {
        return this.http.delete(this.buildUrl(`${id}/api-access/${accessId}`));
    }
    /** GET api/forms/api-access — every grant (FormsAdmin). */
    async getAllApiAccess() {
        return (await this.getAllApiAccessRaw()).data ?? [];
    }
    getAllApiAccessRaw() {
        return this.http.get(this.buildUrl('api-access'));
    }
    // ---- inbound tokens ----
    /** GET api/forms/{id}/inbound-tokens */
    async getInboundTokens(id) {
        return (await this.getInboundTokensRaw(id)).data ?? [];
    }
    getInboundTokensRaw(id) {
        return this.http.get(this.buildUrl(`${id}/inbound-tokens`));
    }
    /** POST api/forms/{id}/inbound-tokens — the plain token is shown once. */
    async createInboundToken(id, request) {
        return unwrap(await this.createInboundTokenRaw(id, request), `Failed to create inbound token for form ${id}`);
    }
    createInboundTokenRaw(id, request) {
        return this.http.post(this.buildUrl(`${id}/inbound-tokens`), request);
    }
    /** DELETE api/forms/{id}/inbound-tokens/{tokenId} */
    async revokeInboundToken(id, tokenId) {
        const response = await this.revokeInboundTokenRaw(id, tokenId);
        return response.data ?? response.success;
    }
    revokeInboundTokenRaw(id, tokenId) {
        return this.http.delete(this.buildUrl(`${id}/inbound-tokens/${tokenId}`));
    }
    // ---- admin capture ----
    /** POST api/forms/{formId}/entries — admin captures an entry on someone's behalf. */
    async createEntry(formId, request) {
        return unwrap(await this.createEntryRaw(formId, request), `Failed to create entry on form ${formId}`);
    }
    createEntryRaw(formId, request) {
        return this.http.post(this.buildUrl(`${formId}/entries`), request);
    }
    // ---- AI ----
    /** POST api/forms/ai/generate — draft a schema from a plain-language description. */
    async aiGenerate(request) {
        return unwrap(await this.aiGenerateRaw(request), 'Failed to generate form');
    }
    aiGenerateRaw(request) {
        return this.http.post(this.buildUrl('ai/generate'), request);
    }
    /** POST api/forms/{id}/ai/summarize-entries */
    async aiSummarizeEntries(id) {
        return unwrap(await this.aiSummarizeEntriesRaw(id), `Failed to summarise entries for form ${id}`);
    }
    aiSummarizeEntriesRaw(id) {
        return this.http.post(this.buildUrl(`${id}/ai/summarize-entries`), {});
    }
    /** POST api/forms/{id}/ai/suggest-member-mapping */
    async aiSuggestMemberMapping(id) {
        return (await this.aiSuggestMemberMappingRaw(id)).data ?? [];
    }
    aiSuggestMemberMappingRaw(id) {
        return this.http.post(this.buildUrl(`${id}/ai/suggest-member-mapping`), {});
    }
}
FormsService.DEFAULT_SERVICE_PATH = 'forms';
FormsService.BASE_PATH = 'api/forms';
//# sourceMappingURL=FormsService.js.map